import * as React from 'react';
import { RouteComponentProps, useHistory } from 'react-router';
import {
  useUpdatePlaceSortMutation,
  useGetPlaceSortLazyQuery,
  useGetConfigTilesQuery,
} from '../generated/graphql';
import { Loading } from '../Loading';
import { ToastContext } from '../components/Toast';
import { GraphQLErrorDialog } from '../components/Dialogs';
import { FormActions } from '../components/FormActions';
import { Input } from '../components/Input';
import { SectionHeader } from '../components/SectionHeader';
import { useValidation } from '../utils/Validation';
import { ValidationRules } from '../utils/ValidationRules';
import './PlaceCreateSortPage.css';

type PlaceCreateSortPageProps = RouteComponentProps<{ placeId: string }> & {
  existingId?: number;
};

/** 新規の登録か既存の編集かで、見出し・送信ボタン・成功の通知の文言を出し分ける */
function getFormTexts(existingId: number | undefined) {
  if (existingId) {
    return {
      title: '補修分類の編集',
      submitLabel: '保存',
      completedMessage: '補修分類を保存しました',
    };
  }
  return {
    title: '補修分類の登録',
    submitLabel: '登録',
    completedMessage: '補修分類を登録しました',
  };
}

export const PlaceCreateSortPage: React.FC<PlaceCreateSortPageProps> = function (
  props
) {
  const placeId = parseInt(props.match.params.placeId);
  const history = useHistory();
  const toastContext = React.useContext(ToastContext);
  const validation = useValidation(ValidationRules.PlaceSort);
  const [shapeTileSchemeId, setShapeTileSchemeId] = React.useState(-1);
  const [name, setName] = validation.useState('name', '');
  const [prefix, setPrefix] = React.useState('');
  const formTexts = getFormTexts(props.existingId);
  const listPath = `/places/${placeId}/sorts`;
  const getConfigResult = useGetConfigTilesQuery({
    onCompleted(data) {
      if (shapeTileSchemeId === -1) {
        setShapeTileSchemeId(data.getConfig.tiles[0].id);
      }
    }
  });
  const [getPlaceSort, getPlaceSortResult] = useGetPlaceSortLazyQuery({
    onCompleted(data) {
      setName(data.getPlaceSort.name);
      setPrefix(data.getPlaceSort.prefix);
      setShapeTileSchemeId(data.getPlaceSort.shape_tile_id);
    },
  });
  const [addPlaceSort, addPlaceSortResult] = useUpdatePlaceSortMutation({
    onCompleted() {
      toastContext.showToast(formTexts.completedMessage, 'success');
      history.push(listPath);
    },
    onError() {
      toastContext.showToast('保存できませんでした。時間をおいて再実行してください。', 'error');
    },
  });

  if (props.existingId && !getPlaceSortResult.called) {
    getPlaceSort({
      variables: { placeSortId: props.existingId },
    });
  }

  if (getPlaceSortResult.called) {
    if (getPlaceSortResult.error) {
      return <GraphQLErrorDialog error={getPlaceSortResult.error} />;
    }

    if (getPlaceSortResult.loading || !getPlaceSortResult.data) {
      return <Loading />;
    }
  }

  if (getConfigResult.loading || !getConfigResult.data) {
    return <Loading />;
  }

  const tiles = getConfigResult.data.getConfig.tiles;

  return (
    <div className="PlaceCreateSortPage-container">
      <Loading open={addPlaceSortResult.loading} />
      <SectionHeader title={formTexts.title} />
      <div className="App-form">
        <div className="PlaceCreateSortPage-input_container">
          <Input
            label="補修分類名"
            required={true}
            type="string"
            onChange={(event) => setName(event.target.value)}
            value={name}
            validationMessage={validation.get('name')}
          />
        </div>
        <div className="PlaceCreateSortPage-input_container">
          <Input
            label="前置文字列"
            type="string"
            onChange={(event) => setPrefix(event.target.value)}
            value={prefix}
          />
        </div>
        <div className="App-input_container PlaceCreateSortPage-input_container">
          <label className="App-label" htmlFor="PlaceCreateSortPage-shape_tile">
            タイルの種類
          </label>
          <select
            id="PlaceCreateSortPage-shape_tile"
            value={shapeTileSchemeId}
            onChange={(event) =>
              setShapeTileSchemeId(parseInt(event.target.value))
            }
          >
            {tiles.map((shapeTile) => (
              <option key={shapeTile.id} value={shapeTile.id}>{shapeTile.name}</option>
            ))}
          </select>
        </div>
        <FormActions
          submitLabel={formTexts.submitLabel}
          submitDisabled={validation.isInvalid || addPlaceSortResult.loading}
          onCancel={() => history.push(listPath)}
          onSubmit={() => {
            addPlaceSort({
              variables: {
                input: {
                  id: props.existingId,
                  place_id: placeId,
                  name: name,
                  prefix: prefix,
                  shape_tile_id: shapeTileSchemeId
                },
              },
            });
          }}
        />
      </div>
    </div>
  );
};
